import { useEffect, useState, type FormEvent } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

/** Finalidades do filtro (SPEC-018, §1, decisão 2), pelo `finNFe` gravado. */
export const FINALIDADES = [
  { value: '1', label: 'Normal' },
  { value: '4', label: 'Devolução' },
  { value: '5', label: 'Nota de crédito' },
  { value: '6', label: 'Nota de débito' },
] as const;

const TODAS = 'TODAS';

interface BuscaEFinalidadeProps {
  busca: string;
  finalidade: string;
  desabilitado?: boolean;
  onAplicar: (filtro: { busca: string; finalidade: string }) => void;
}

/**
 * A busca única e o filtro de finalidade do topo de Documentos Fiscais (SPEC-018, §3, itens 1 e 2).
 * A busca aceita chave (inteira ou o começo), número, CNPJ ou nome do participante, sem escolher o
 * modelo: quem decide o que é cada coisa é o servidor. Aplica ao apertar Enter; a finalidade, ao escolher.
 */
export function BuscaEFinalidade({
  busca,
  finalidade,
  desabilitado,
  onAplicar,
}: BuscaEFinalidadeProps) {
  const [texto, setTexto] = useState(busca);

  useEffect(() => setTexto(busca), [busca]);

  const enviar = (evento: FormEvent) => {
    evento.preventDefault();
    onAplicar({ busca: texto.trim(), finalidade });
  };

  return (
    <form className="flex flex-wrap items-center gap-2" onSubmit={enviar} role="search">
      <div className="relative w-full sm:w-96">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          aria-label="Buscar por chave, número, CNPJ ou participante"
          placeholder="Chave, número, CNPJ ou participante"
          className="pl-9"
          value={texto}
          disabled={desabilitado}
          onChange={evento => setTexto(evento.target.value)}
        />
      </div>
      <Select
        value={finalidade || TODAS}
        disabled={desabilitado}
        onValueChange={valor =>
          onAplicar({ busca: texto.trim(), finalidade: valor === TODAS ? '' : valor })
        }
      >
        <SelectTrigger className="w-48" aria-label="Finalidade">
          <SelectValue placeholder="Finalidade" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={TODAS}>Todas as finalidades</SelectItem>
          {FINALIDADES.map(opcao => (
            <SelectItem key={opcao.value} value={opcao.value}>
              {opcao.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </form>
  );
}
